/**
 * LiveAvatar Component - Upgraded from Interactive Avatar
 * 
 * This component uses the new HeyGen LiveAvatar SDK for real-time avatar interactions.
 * Replaces the deprecated @heygen/streaming-avatar with @heygen/liveavatar-web-sdk
 * 
 * Features:
 * - Sandbox mode for free development/testing (no credits consumed)
 * - Full mode for production use
 * - Voice chat and text chat support
 * - Improved session management
 * 
 * Usage:
 * import { LiveAvatarComponent } from './LiveAvatarComponent';
 * 
 * <LiveAvatarComponent 
 *   apiKey="your-liveavatar-api-key"
 *   useSandbox={true}  // Set to false for production
 *   onError={(error) => console.error(error)}
 * />
 * 
 * Documentation: https://docs.liveavatar.com
 */

import React, { 
  useEffect, 
  useRef, 
  useState, 
  useCallback
} from "react";
import { Room, RoomEvent } from 'livekit-client';
import { callAI } from '../../../services/aiChatService';
import {
  CContainer,
  CRow,
  CCol,
  CFormInput,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CButton,
  CCard,
  CCardBody,
  CSpinner,
  CFormLabel,
  CAlert,
  CFormCheck
} from '@coreui/react';

// ===================== CONSTANTS =====================

// LiveAvatar API Base URL (different from HeyGen)
const LIVEAVATAR_API_URL = "https://api.liveavatar.com";

// Available avatars for LiveAvatar
// Note: In sandbox mode, only Wayne is available
const LIVEAVATAR_AVATARS = [
  {
    id: "dd73ea75-1218-4ef3-92ce-606d5f7fbc0a",
    name: "Wayne (Sandbox)",
    sandbox_only: false // Available in both modes
  },
  // Add more avatars here after migrating from HeyGen
  // You can get avatar IDs from app.liveavatar.com
];

const LANGUAGES = [
  { label: "English", value: "en" },
  { label: "Spanish", value: "es" },
  { label: "French", value: "fr" },
  { label: "German", value: "de" },
  { label: "Chinese", value: "zh" },
  { label: "Japanese", value: "ja" },
  { label: "Korean", value: "ko" },
  { label: "Portuguese", value: "pt" },
  { label: "Italian", value: "it" },
];

// ===================== ICONS =====================

const MicIcon = ({ size = 24 }) => (
  <svg fill="none" height={size} viewBox="0 0 20 20" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10V5C12.5 3.61929 11.3807 2.5 10 2.5C8.61929 2.5 7.5 3.61929 7.5 5V10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 10C15 12.7614 12.7614 15 10 15C7.23858 15 5 12.7614 5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 15V17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MicOffIcon = ({ size = 24 }) => (
  <svg fill="none" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 9V12C9 13.6569 10.3431 15 12 15C12.8107 15 13.5428 14.6734 14.0711 14.1451M15 10.6V6C15 4.34315 13.6569 3 12 3C10.6838 3 9.58254 3.8479 9.17071 5.01624" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 16.95C15.5 18.2 13.8 19 12 19C8.13401 19 5 15.866 5 12V11M19 11V12C19 12.6 18.9 13.2 18.8 13.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 19V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 23H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SendIcon = ({ size = 24 }) => (
  <svg fill="none" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = ({ size = 24 }) => (
  <svg fill="none" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ===================== SESSION STATES =====================

const SessionState = {
  INACTIVE: "inactive",
  CREATING_TOKEN: "creating_token",
  STARTING: "starting",
  CONNECTED: "connected",
  ERROR: "error"
};

// ===================== MAIN COMPONENT =====================

export const LiveAvatarComponent = ({ 
  apiKey, 
  useSandbox = true, // Default to sandbox for development
  onError,
  customStyles = {}
}) => {
  // Session state
  const [sessionState, setSessionState] = useState(SessionState.INACTIVE);
  const [sessionId, setSessionId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [liveKitUrl, setLiveKitUrl] = useState(null);
  const [liveKitToken, setLiveKitToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Configuration state
  const [selectedAvatar, setSelectedAvatar] = useState(LIVEAVATAR_AVATARS[0]);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [isSandboxMode, setIsSandboxMode] = useState(useSandbox);
  const [voiceChatEnabled, setVoiceChatEnabled] = useState(true);
  
  // Chat state
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isMuted, setIsMuted] = useState(true);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);
  
  // Refs
  const videoRef = useRef(null);
  const liveAvatarSessionRef = useRef(null);
  
  // ===================== API FUNCTIONS =====================
  
  /**
   * Create a session token from LiveAvatar API
   * In sandbox mode, no credits are consumed
   */
  const createSessionToken = async () => {
    try {
      setSessionState(SessionState.CREATING_TOKEN);
      setErrorMessage(null);
      
      // Log debug info
      const maskedKey = apiKey ? `${apiKey.substring(0, 8)}...${apiKey.substring(apiKey.length - 4)}` : 'NOT SET';
      console.log("🔑 LiveAvatar API Key:", maskedKey);
      console.log("🧪 Sandbox Mode:", isSandboxMode);
      
      if (!apiKey) {
        throw new Error("LiveAvatar API Key is not set. Check VITE_LIVEAVATAR_API_KEY in .env");
      }
      
      // Build request body
      const requestBody = {
        mode: "FULL",
        avatar_id: selectedAvatar.id,
        avatar_persona: {
          language: selectedLanguage
        }
      };
      
      // Add sandbox flag if enabled
      if (isSandboxMode) {
        requestBody.is_sandbox = true;
        // In sandbox mode, only Wayne avatar is available
        requestBody.avatar_id = "dd73ea75-1218-4ef3-92ce-606d5f7fbc0a";
      }
      
      console.log("📤 Creating LiveAvatar session token");
      
      const response = await fetch(`${LIVEAVATAR_API_URL}/v1/sessions/token`, {
        method: "POST",
        headers: {
          "X-API-KEY": apiKey,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(requestBody)
      });
      
      const responseData = await response.json();
      
      // Check for API-level errors (code !== 1000 means error)
      if (responseData.code !== 1000) {
        throw new Error(responseData.message || `Failed to create session: code ${responseData.code}`);
      }
      
      if (!response.ok) {
        throw new Error(responseData.message || responseData.detail || `Failed to create session: ${response.status}`);
      }
      
      // Extract from the nested data object
      const tokenData = responseData.data;
      
      if (!tokenData || !tokenData.session_id || !tokenData.session_token) {
        throw new Error("Invalid response: missing session_id or session_token");
      }
      
      return {
        sessionId: tokenData.session_id,
        sessionToken: tokenData.session_token
      };
    } catch (error) {
      console.error("❌ Error creating session token:", error);
      setErrorMessage(error.message);
      setSessionState(SessionState.ERROR);
      if (onError) onError(error);
      throw error;
    }
  };
  
  /**
   * Start the LiveAvatar session
   */
  const startSession = async () => {
    try {
      setSessionState(SessionState.STARTING);
      
      // First, get the session token
      const { sessionId, sessionToken } = await createSessionToken();
      setSessionId(sessionId);
      setSessionToken(sessionToken);
      
      console.log("🚀 Starting LiveAvatar session:", sessionId);
      
      // Start the session
      const startResponse = await fetch(`${LIVEAVATAR_API_URL}/v1/sessions/start`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${sessionToken}`,
          "Accept": "application/json"
        }
      });
      
      const startResponseData = await startResponse.json();
      console.log("📡 LiveAvatar Start Response:", startResponseData);
      
      // Check for API-level errors
      if (startResponseData.code && startResponseData.code !== 1000) {
        throw new Error(startResponseData.message || `Failed to start session: code ${startResponseData.code}`);
      }
      
      if (!startResponse.ok) {
        throw new Error(startResponseData.message || startResponseData.detail || `Failed to start session: ${startResponse.status}`);
      }
      
      // Extract LiveKit credentials from nested data object
      const sessionData = startResponseData.data || startResponseData;
      console.log("📦 Session Data:", sessionData);
      
      // Store LiveKit credentials
      setLiveKitUrl(sessionData.livekit_url);
      setLiveKitToken(sessionData.livekit_client_token);
      
      // Initialize LiveAvatar SDK session if available
      // Note: You may need to import and use the SDK here
      // For now, we'll use the LiveKit meet URL for quick testing
      
      setSessionState(SessionState.CONNECTED);
      console.log("✅ LiveAvatar session started successfully!");
      
      // Add welcome message
      setMessages([{
        id: Date.now(),
        sender: "SYSTEM",
        content: isSandboxMode 
          ? "🧪 Connected in Sandbox Mode (1 minute limit, no credits used)"
          : "✅ Connected to LiveAvatar"
      }]);
      
    } catch (error) {
      console.error("❌ Error starting session:", error);
      setErrorMessage(error.message);
      setSessionState(SessionState.ERROR);
      if (onError) onError(error);
    }
  };
  
  /**
   * Stop the LiveAvatar session
   */
  const stopSession = async () => {
    try {
      if (sessionToken && sessionId) {
        console.log("🛑 Stopping LiveAvatar session:", sessionId);
        
        await fetch(`${LIVEAVATAR_API_URL}/v1/sessions/stop`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${sessionToken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ session_id: sessionId })
        });
      }
      
      // Clean up LiveKit room if connected
      if (liveAvatarSessionRef.current?.disconnect) {
        await liveAvatarSessionRef.current.disconnect();
        liveAvatarSessionRef.current = null;
      }
      
    } catch (error) {
      console.error("Error stopping session:", error);
    } finally {
      // Reset state
      setSessionState(SessionState.INACTIVE);
      setSessionId(null);
      setSessionToken(null);
      setLiveKitUrl(null);
      setLiveKitToken(null);
      setMessages([]);
      setIsMuted(true);
    }
  };
  
  /**
   * Send a text message to the avatar
   */
  const sendMessage = async () => {
    if (!message.trim() || !sessionToken || !sessionId) return;
    
    try {
      // Add user message to chat
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: "USER",
        content: message
      }]);
      
      const userText = message;
      setMessage("");

      // The reply is generated by the mieGPT endpoint (chat.mieros.cloud/gptj), not LiveAvatar's built-in LLM.
      const reply = await callAI({
        systemMessage: "You are a friendly AI avatar for MiEROS. Reply conversationally in one to three short sentences.",
        prompt: userText
      });

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        sender: "AVATAR",
        content: reply
      }]);

      // Have the avatar speak the generated reply verbatim.
      const response = await fetch(`${LIVEAVATAR_API_URL}/v1/sessions/task`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${sessionToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: sessionId,
          text: reply,
          task_type: "repeat"
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to send message");
      }
      
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender: "SYSTEM",
        content: `❌ Error: ${error.message}`
      }]);
    }
  };
  
  /**
   * Toggle microphone mute state
   */
  const toggleMute = () => {
    setIsMuted(!isMuted);
    // TODO: Implement actual mute/unmute with LiveAvatar SDK
  };
  
  // Connect LiveKit video when session credentials are available
  useEffect(() => {
    if (sessionState !== SessionState.CONNECTED || !liveKitUrl || !liveKitToken || !videoRef.current) {
      return undefined;
    }

    const room = new Room({ adaptiveStream: true, dynacast: true });
    const videoElement = videoRef.current;

    const onTrackSubscribed = (track) => {
      if (track.kind === 'video') {
        track.attach(videoElement);
      }
    };

    const onTrackUnsubscribed = (track) => {
      track.detach();
    };

    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);

    room.connect(liveKitUrl, liveKitToken).catch((error) => {
      console.error('LiveKit connection failed:', error);
      setErrorMessage(error.message);
    });

    liveAvatarSessionRef.current = room;

    return () => {
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
      room.disconnect();
      if (videoElement) {
        videoElement.srcObject = null;
      }
      if (liveAvatarSessionRef.current === room) {
        liveAvatarSessionRef.current = null;
      }
    };
  }, [sessionState, liveKitUrl, liveKitToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSession();
    };
  }, []);
  
  // ===================== RENDER =====================
  
  // Render configuration panel (when not connected)
  const renderConfigPanel = () => (
    <div style={{ 
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      <h4 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
        🎭 LiveAvatar Configuration
      </h4>
      
      {/* Sandbox Mode Toggle */}
      <div style={{
        padding: '1rem',
        backgroundColor: isSandboxMode ? 'var(--success-bg)' : 'var(--warning-bg)',
        borderRadius: '8px',
        border: `1px solid ${isSandboxMode ? 'var(--success-border)' : 'var(--warning-border)'}`
      }}>
        <CFormCheck
          id="sandbox-mode"
          label={
            <span>
              <strong>🧪 Sandbox Mode</strong>
              <br />
              <small className="mb-2">
                {isSandboxMode 
                  ? "Free development mode - no credits consumed (1 min limit)"
                  : "Production mode - credits will be consumed"}
              </small>
            </span>
          }
          checked={isSandboxMode}
          onChange={(e) => setIsSandboxMode(e.target.checked)}
        />
      </div>
      
      {/* Avatar Selection */}
      <div>
        <CFormLabel>Avatar</CFormLabel>
        <CDropdown style={{ width: '100%' }}>
          <CDropdownToggle color="secondary" style={{ width: '100%', textAlign: 'left' }}>
            {selectedAvatar.name}
          </CDropdownToggle>
          <CDropdownMenu style={{ width: '100%' }}>
            {LIVEAVATAR_AVATARS
              .filter(avatar => !isSandboxMode || !avatar.sandbox_only)
              .map(avatar => (
                <CDropdownItem 
                  key={avatar.id}
                  active={avatar.id === selectedAvatar.id}
                  onClick={() => setSelectedAvatar(avatar)}
                >
                  {avatar.name}
                </CDropdownItem>
              ))}
          </CDropdownMenu>
        </CDropdown>
        {isSandboxMode && (
          <small className="mb-2">
            Note: Only Wayne avatar is available in sandbox mode
          </small>
        )}
      </div>
      
      {/* Language Selection */}
      <div>
        <CFormLabel>Language</CFormLabel>
        <CDropdown style={{ width: '100%' }}>
          <CDropdownToggle color="secondary" style={{ width: '100%', textAlign: 'left' }}>
            {LANGUAGES.find(l => l.value === selectedLanguage)?.label || "English"}
          </CDropdownToggle>
          <CDropdownMenu style={{ width: '100%' }}>
            {LANGUAGES.map(lang => (
              <CDropdownItem 
                key={lang.value}
                active={lang.value === selectedLanguage}
                onClick={() => setSelectedLanguage(lang.value)}
              >
                {lang.label}
              </CDropdownItem>
            ))}
          </CDropdownMenu>
        </CDropdown>
      </div>
      
      {/* Voice Chat Toggle */}
      <CFormCheck
        id="voice-chat"
        label="Enable Voice Chat"
        checked={voiceChatEnabled}
        onChange={(e) => setVoiceChatEnabled(e.target.checked)}
      />
      
      {/* Error Display */}
      {errorMessage && (
        <CAlert color="danger">
          <strong>Error:</strong> {errorMessage}
        </CAlert>
      )}
      
      {/* Start Button */}
      <CButton
        color="primary"
        size="lg"
        onClick={startSession}
        disabled={sessionState !== SessionState.INACTIVE && sessionState !== SessionState.ERROR}
        style={{ marginTop: '1rem' }}
      >
        {sessionState === SessionState.CREATING_TOKEN || sessionState === SessionState.STARTING ? (
          <>
            <CSpinner size="sm" className="me-2" />
            Starting...
          </>
        ) : (
          <>🚀 Start {isSandboxMode ? "Sandbox" : "Live"} Session</>
        )}
      </CButton>
    </div>
  );
  
  // Render connected session view
  const renderSessionView = () => (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      ...customStyles.containerStyle
    }}>
      {/* Video/Avatar Area */}
      <div style={{ 
        flex: 1, 
        backgroundColor: '#1a1a1a', 
        position: 'relative',
        minHeight: '300px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Close button */}
        <CButton
          color="danger"
          size="sm"
          style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}
          onClick={stopSession}
        >
          <CloseIcon size={16} /> End Session
        </CButton>
        
        {/* Sandbox indicator */}
        {isSandboxMode && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            backgroundColor: 'var(--success)',
            color: 'var(--dark)',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            🧪 SANDBOX MODE
          </div>
        )}
        
        {/* LiveKit video stream */}
        {liveKitUrl && liveKitToken ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }}
          />
        ) : (
          <CSpinner color="light" />
        )}
      </div>
      
      {/* Message History */}
      <div style={{
        height: '150px',
        overflowY: 'auto',
        padding: '10px',
        backgroundColor: 'var(--primary)',
        borderTop: '1px solid var(--border-subtle)'
      }}>
        {messages.map(msg => (
          <div 
            key={msg.id}
            style={{
              marginBottom: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              backgroundColor: msg.sender === 'USER' ? 'var(--info-bg)' :
                             msg.sender === 'AVATAR' ? 'var(--glass-bg)' : 'var(--dark-alt)',
              marginLeft: msg.sender === 'USER' ? 'auto' : 0,
              marginRight: msg.sender === 'USER' ? 0 : 'auto',
              maxWidth: '80%'
            }}
          >
            <small style={{ color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
              {msg.sender === 'USER' ? 'You' : msg.sender === 'AVATAR' ? 'Avatar' : 'System'}
            </small>
            {msg.content}
          </div>
        ))}
      </div>
      
      {/* Input Controls */}
      <div style={{
        padding: '10px',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex',
        gap: '10px',
        alignItems: 'center'
      }}>
        {voiceChatEnabled && (
          <CButton
            color={isMuted ? "secondary" : "primary"}
            onClick={toggleMute}
            style={{ padding: '8px 12px' }}
          >
            {isMuted ? <MicOffIcon size={20} /> : <MicIcon size={20} />}
          </CButton>
        )}
        
        <CFormInput
          type="text"
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          style={{ flex: 1 }}
        />
        
        <CButton
          color="primary"
          onClick={sendMessage}
          disabled={!message.trim()}
        >
          <SendIcon size={20} />
        </CButton>
      </div>
    </div>
  );
  
  return (
    <CCard style={{ 
      overflow: 'hidden',
      ...customStyles.cardStyle
    }}>
      <CCardBody style={{ padding: 0 }}>
        {sessionState === SessionState.CONNECTED ? renderSessionView() : renderConfigPanel()}
      </CCardBody>
    </CCard>
  );
};

export default LiveAvatarComponent;



